/**
 * OCHAT: el contenido de la guía en la propia aplicación.
 *
 * POR QUÉ ESTÁ ACÁ Y NO EN LOS ARCHIVOS DE i18n
 * Esto es contenido editorial de Ochat, no rótulos de interfaz. Los párrafos
 * largos en JSON son incómodos de editar y de revisar en un diff, y no hay una
 * clave de upstream que estemos sobreescribiendo. Los rótulos de la página sí van
 * por i18n, en ochat/i18n/<locale>/guide.json.
 *
 * La estructura queda preparada para más idiomas: si algún día hace falta, cada
 * tema pasa a ser `{ es: {...}, en: {...} }` y la página elige por locale. Hoy
 * todas las instancias de Ochat operan en español.
 *
 * LA CLAVE DE CADA TEMA ES EL `feature-name` DE UPSTREAM
 * Las pantallas de configuración pasan `feature-name` a BaseSettingsHeader, que
 * resuelve el enlace con getHelpUrlForFeature(). El overlay de ochat/guide/helpUrl.js
 * consulta este objeto por esa misma clave, así que agregar un tema acá es lo único
 * que hace falta para que el enlace "Aprender más" de esa pantalla deje de ir a la
 * documentación de Chatwoot y venga a la guía propia.
 *
 * Los temas cubren lo que ve un administrador de cuenta. El resto de las secciones
 * está reservado al super admin (ver ochat/config/reservedSections.js), así que su
 * enlace no lo ve nadie más que el operador.
 */

/** @type {Record<string, {title: string, summary: string, sections: Array<{heading: string, body?: string, steps?: string[], items?: Array<{term: string, description: string}>}>}>} */
export const GUIDE_TOPICS = {
  macros: {
    title: 'Macros',
    summary:
      'Una macro es una secuencia de acciones que se ejecuta de una sola vez sobre una conversación. Sirve para no repetir a mano los mismos cinco pasos cada vez que se cierra un caso.',
    sections: [
      {
        heading: '¿Cuándo conviene una macro?',
        body: 'Cuando notás que siempre hacés lo mismo en cierto tipo de conversación: responder algo, ponerle una etiqueta, asignarla a alguien y resolverla. Si son dos clics, no vale la pena. Si son cinco y se repiten todos los días, sí.',
      },
      {
        heading: 'Cómo se crea',
        steps: [
          'Entrá a Configuración → Macros y hacé clic en "Crear macro".',
          'Ponele un nombre que se entienda desde la conversación, porque es lo que va a ver la persona que la ejecute. "Cerrar consulta de precio" es mejor que "Macro 3".',
          'Elegí la visibilidad: "Global" la ven todos los agentes de la cuenta, "Personal" solo vos.',
          'Agregá las acciones en el orden en que querés que pasen. Cada acción se agrega con el botón de "+".',
          'Guardá. La macro ya aparece en el panel derecho de cualquier conversación.',
        ],
      },
      {
        heading: 'Las acciones que más se usan',
        items: [
          {
            term: 'Enviar un mensaje',
            description:
              'Manda un mensaje al contacto. Es el reemplazo de copiar y pegar el mismo texto.',
          },
          {
            term: 'Agregar una nota privada',
            description:
              'Deja una nota que el contacto no ve. Útil para dejar registro de por qué se cerró.',
          },
          {
            term: 'Asignar a un agente o equipo',
            description:
              'Deriva la conversación. Combinado con etiquetas es la forma de armar un circuito de derivación.',
          },
          {
            term: 'Agregar o quitar etiquetas',
            description:
              'Clasifica la conversación para poder medirla después.',
          },
          {
            term: 'Cambiar el estado',
            description:
              'Resuelve, pone en pendiente o silencia la conversación. Casi siempre es la última acción de la macro.',
          },
        ],
      },
      {
        heading: 'Cómo se ejecuta',
        body: 'Abrí una conversación y buscá el bloque de Macros en el panel de la derecha. Elegí la macro y confirmá. Las acciones se aplican en orden sobre esa conversación. Si algo sale mal, queda registrado en la conversación y podés revertirlo a mano.',
      },
      {
        heading: 'Un ejemplo completo',
        body: 'Macro "Derivar a Ventas": agrega la etiqueta "venta", asigna al equipo Ventas, envía el mensaje "Te paso con el equipo comercial, en un rato te escriben" y deja la conversación abierta. Tres clics del agente pasan a ser uno.',
      },
      {
        heading: 'Errores comunes',
        items: [
          {
            term: 'Poner "Resolver" antes de enviar el mensaje',
            description:
              'Las acciones corren en orden. Si resolvés primero, el mensaje sale igual pero la conversación ya se cerró y el contacto puede quedar sin seguimiento.',
          },
          {
            term: 'Macros personales que nadie más puede usar',
            description:
              'Si la macro le sirve al equipo, ponela como Global. Una macro personal se pierde cuando esa persona deja de estar.',
          },
        ],
      },
    ],
  },

  canned_responses: {
    title: 'Respuestas predeterminadas',
    summary:
      'Textos guardados que un agente inserta escribiendo un atajo. Es lo más rápido de configurar y lo que más tiempo ahorra por día.',
    sections: [
      {
        heading: 'La diferencia con una macro',
        body: 'Una respuesta predeterminada solo escribe texto en el cuadro de mensaje: no etiqueta, no asigna, no resuelve. El agente puede editarla antes de enviar. Si además necesitás que pasen otras cosas, eso es una macro.',
      },
      {
        heading: 'Cómo se crea',
        steps: [
          'Entrá a Configuración → Respuestas predeterminadas y hacé clic en "Agregar".',
          'En "Atajo" poné una palabra corta y sin espacios. Es lo que el agente va a tipear.',
          'En "Contenido" escribí el texto. Podés usar negrita, listas y enlaces.',
          'Guardá. Está disponible al instante para todos los agentes de la cuenta.',
        ],
      },
      {
        heading: 'Cómo se usa en una conversación',
        body: 'En el cuadro de mensaje, escribí "/" y el atajo. Aparece un listado con las coincidencias; elegí una y el texto se inserta. Después se puede editar antes de enviar, que es justamente la idea: la respuesta es el punto de partida, no el mensaje final.',
      },
      {
        heading: 'Qué hace una buena respuesta predeterminada',
        items: [
          {
            term: 'Atajos que se adivinan',
            description:
              'Usá el tema, no un número. "/envios" se recuerda; "/r1" no. Si son varias del mismo tema, un prefijo común ayuda: "/pago-transferencia", "/pago-tarjeta".',
          },
          {
            term: 'Dejar los huecos marcados',
            description:
              'Si el texto necesita un dato, marcalo de forma visible, por ejemplo con corchetes. Así el agente ve de una que tiene que completarlo y no envía la plantilla cruda.',
          },
          {
            term: 'Que no suene a robot',
            description:
              'El agente la va a editar de todas formas. Escribila como hablás, no como un formulario.',
          },
        ],
      },
    ],
  },

  team_management: {
    title: 'Equipos',
    summary:
      'Un equipo es un grupo de agentes al que se le puede asignar una conversación entera, sin elegir a una persona concreta.',
    sections: [
      {
        heading: 'Para qué sirve',
        body: 'Para derivar por tema en lugar de por persona. En vez de acordarse de quién atiende facturación, la conversación se asigna al equipo Facturación y la toma quien esté disponible. También sirve para filtrar: cada equipo tiene su propia bandeja en la barra lateral.',
      },
      {
        heading: 'Cómo se crea',
        steps: [
          'Entrá a Configuración → Equipos y hacé clic en "Crear equipo".',
          'Ponele nombre y descripción. El nombre aparece en la barra lateral, así que conviene que sea corto.',
          'Tildá "Asignar automáticamente" si querés que las conversaciones que lleguen al equipo se repartan solas entre sus miembros.',
          'Elegí los agentes que lo integran y guardá.',
        ],
      },
      {
        heading: 'Cómo se usa',
        items: [
          {
            term: 'Desde una conversación',
            description:
              'En el panel de la derecha, en "Equipo", elegí el equipo. La conversación queda visible en la bandeja de ese equipo.',
          },
          {
            term: 'Desde una macro',
            description:
              'La acción "Asignar a un equipo" hace lo mismo en automático. Es la forma de armar derivaciones de un clic.',
          },
          {
            term: 'Desde una automatización',
            description:
              'Si necesitás que la derivación pase sin intervención de nadie, eso se configura en Automatización. Esa sección está reservada al administrador de la plataforma: pedísela y te la dejamos armada.',
          },
        ],
      },
      {
        heading: 'Cosas a tener en cuenta',
        items: [
          {
            term: 'Un agente puede estar en varios equipos',
            description:
              'No hay que elegir. La misma persona puede estar en Ventas y en Soporte.',
          },
          {
            term: 'Asignar a un equipo no asigna a una persona',
            description:
              'Si "Asignar automáticamente" está apagado, la conversación queda en la bandeja del equipo sin dueño hasta que alguien la tome. Vale revisar esa bandeja para que no se acumule.',
          },
        ],
      },
    ],
  },

  labels: {
    title: 'Etiquetas',
    summary:
      'Las etiquetas clasifican conversaciones. Es lo que después permite responder "¿de qué nos hablaron este mes?" con un número en vez de una impresión.',
    sections: [
      {
        heading: 'Para qué sirven',
        body: 'Para tres cosas: filtrar la bandeja, medir por tema, y disparar automatizaciones. La tercera es la que más se subestima: una etiqueta puede ser la condición que decide una derivación.',
      },
      {
        heading: 'Cómo se crea',
        steps: [
          'Entrá a Configuración → Etiquetas y hacé clic en "Crear etiqueta".',
          'Ponele un nombre en minúscula y sin acentos. Se usa mucho al escribir y conviene que sea rápido de tipear.',
          'Elegí un color. Sirve para leer la bandeja de un pantallazo, así que agrupá por familia de color.',
          'Tildá "Mostrar en la barra lateral" si querés que tenga su propia bandeja.',
        ],
      },
      {
        heading: 'Cómo mantenerlas usables',
        items: [
          {
            term: 'Pocas y con criterio',
            description:
              'Veinte etiquetas que nadie recuerda son peor que seis que todos usan. Si dos significan casi lo mismo, unificalas.',
          },
          {
            term: 'Un criterio por etiqueta',
            description:
              'Mezclar tema ("envios") con estado ("urgente") en la misma lista funciona, pero conviene decidirlo a propósito y que el equipo lo sepa.',
          },
          {
            term: 'Renombrar es seguro, borrar no',
            description:
              'Cambiarle el nombre a una etiqueta conserva las conversaciones que la tenían. Borrarla la saca de todas, y eso no se recupera: los reportes históricos de ese tema quedan sin dato.',
          },
        ],
      },
    ],
  },

  agents: {
    title: 'Agentes',
    summary:
      'Los agentes son las personas que atienden. Desde acá se los invita, se les cambia el rol y se los da de baja.',
    sections: [
      {
        heading: 'Cómo se invita a alguien',
        steps: [
          'Entrá a Configuración → Agentes y hacé clic en "Agregar agente".',
          'Completá nombre y correo. Al correo le llega la invitación para que se ponga su propia contraseña.',
          'Elegí el rol: "Agente" atiende conversaciones; "Administrador" además configura la cuenta.',
          'Confirmá. Hasta que acepte la invitación, la persona figura como pendiente.',
        ],
      },
      {
        heading: 'Los dos roles',
        items: [
          {
            term: 'Agente',
            description:
              'Ve y responde conversaciones, usa macros y respuestas predeterminadas, gestiona contactos. No entra a la configuración de la cuenta.',
          },
          {
            term: 'Administrador',
            description:
              'Todo lo del agente, más Equipos, Etiquetas, Macros, Respuestas predeterminadas, Agentes y los datos de la cuenta. La configuración de canales, integraciones, automatizaciones y bots está reservada al administrador de la plataforma.',
          },
        ],
      },
      {
        heading: 'Dar de baja a alguien',
        body: 'Quitar un agente de la cuenta libera las conversaciones que tenía asignadas: quedan sin dueño, no se borran. Conviene reasignarlas antes para que no se pierdan de vista. El historial de mensajes que escribió se conserva siempre.',
      },
      {
        heading: 'Si alguien no recibe la invitación',
        body: 'Casi siempre es el filtro de correo no deseado. Que lo revise ahí antes de reenviar. Si tampoco está, verificá que el correo esté bien escrito: la invitación se manda a lo que se cargó, sin validación de que exista.',
      },
    ],
  },
};

/** @param {string} featureName clave del `feature-name` de upstream */
export const getGuideTopic = featureName => GUIDE_TOPICS[featureName];

/** Los temas disponibles, para el índice de la página. */
export const guideTopicList = () =>
  Object.entries(GUIDE_TOPICS).map(([key, topic]) => ({
    key,
    title: topic.title,
  }));

export default GUIDE_TOPICS;
